/**
 * EXIF Data Extraction Contract
 * Handles extraction and processing of EXIF metadata from image files
 */

export interface ExifCameraSettings {
  aperture?: string // f/2.8, f/8.0
  shutterSpeed?: string // 1/60, 1/250, 2s
  iso?: number // 100, 800, 3200
  focalLength?: string // 35mm, 85mm
  exposureMode?: string // Auto, Manual, Aperture Priority
  meteringMode?: string // Matrix, Center-weighted, Spot
  whiteBalance?: string // Auto, Daylight, Tungsten
  flash?: string // Off, Fired, Auto
  exposureCompensation?: string // +0.7, -1.3
  sceneMode?: string // Portrait, Landscape, Sports
}

export interface ExifEquipmentInfo {
  cameraMake?: string // Canon, Nikon, Sony
  cameraModel?: string // EOS R5, D850, A7R IV
  lensModel?: string // RF 24-70mm f/2.8L IS USM
  serialNumber?: string
  firmwareVersion?: string
  software?: string // Image processing software
}

export interface ExifImageProperties {
  width?: number
  height?: number
  orientation?: number // 1-8 (EXIF orientation values)
  colorSpace?: string // sRGB, Adobe RGB
  bitDepth?: number
  compression?: string
  pixelXDimension?: number
  pixelYDimension?: number
}

export interface ExifLocationData {
  latitude?: number
  longitude?: number
  altitude?: number
  gpsDirection?: number
  gpsDateStamp?: string
  gpsTimeStamp?: string
}

export interface ExifDateTimeInfo {
  dateTimeOriginal?: Date // When photo was taken
  dateTimeDigitized?: Date // When image was digitized
  createDate?: Date
  modifyDate?: Date
  offsetTime?: string // Timezone offset
  offsetTimeOriginal?: string
  offsetTimeDigitized?: string
}

export interface ExifTechnicalData {
  subjectDistance?: string
  lightSource?: string
  digitalZoomRatio?: number
  saturation?: string
  contrast?: string
  sharpness?: string
  gainControl?: string
  sensingMethod?: string
  fileSource?: string
  sceneType?: string
}

export interface ExifData {
  // Core camera settings
  camera: ExifCameraSettings
  
  // Equipment information
  equipment: ExifEquipmentInfo
  
  // Image properties
  image: ExifImageProperties
  
  // Location data (if available)
  location?: ExifLocationData
  
  // Date and time information
  dateTime: ExifDateTimeInfo
  
  // Additional technical data
  technical: ExifTechnicalData
  
  // Raw EXIF tags (for debugging/advanced use)
  rawTags?: Record<string, any>
  
  // Processing metadata
  extractedAt: Date
  hasErrors: boolean
  errors?: string[]
}

export interface ExifExtractionOptions {
  includeRawTags?: boolean // Include all raw EXIF tags
  includeThumbnail?: boolean // Extract embedded thumbnail
  validateData?: boolean // Validate extracted data
  convertCoordinates?: boolean // Convert GPS coordinates to decimal degrees
}

export interface ExifKeywordSuggestions {
  camera?: string[] // ["Canon", "DSLR", "full-frame"]
  lens?: string[] // ["telephoto", "zoom", "portrait lens"]
  settings?: string[] // ["shallow depth of field", "high ISO", "long exposure"]
  location?: string[] // Location-based keywords from GPS
  technical?: string[] // ["macro", "wide angle", "handheld"]
}

/**
 * EXIF Extractor Service Interface
 */
export interface ExifExtractor {
  /**
   * Extracts EXIF data from an image file
   * @param file - Image file to extract EXIF data from
   * @param options - Extraction options
   * @returns Promise resolving to EXIF data
   */
  extractExifData(file: File, options?: ExifExtractionOptions): Promise<ExifData>

  /**
   * Extracts EXIF data from image blob/buffer
   * @param buffer - Image data as ArrayBuffer
   * @param options - Extraction options
   * @returns Promise resolving to EXIF data
   */
  extractExifFromBuffer(buffer: ArrayBuffer, options?: ExifExtractionOptions): Promise<ExifData>

  /**
   * Generates keyword suggestions based on EXIF data
   * @param exifData - Extracted EXIF data
   * @returns Keyword suggestions organized by category
   */
  generateKeywordSuggestions(exifData: ExifData): ExifKeywordSuggestions

  /**
   * Formats EXIF data for display
   * @param exifData - Extracted EXIF data
   * @returns Human-readable EXIF information
   */
  formatForDisplay(exifData: ExifData): Record<string, string>

  /**
   * Checks if file contains EXIF data
   * @param file - Image file to check
   * @returns Promise resolving to boolean
   */
  hasExifData(file: File): Promise<boolean>

  /**
   * Gets supported file types for EXIF extraction
   * @returns Array of supported MIME types
   */
  getSupportedTypes(): string[]

  /**
   * Validates extracted EXIF data
   * @param exifData - EXIF data to validate
   * @returns Validation result with any issues found
   */
  validateExifData(exifData: ExifData): ExifValidationResult
}

export interface ExifValidationResult {
  isValid: boolean
  warnings: string[]
  errors: string[]
  suggestions: string[]
}

/**
 * Error types for EXIF extraction
 */
export class ExifExtractionError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: Record<string, any>
  ) {
    super(message)
    this.name = 'ExifExtractionError'
  }
}

/**
 * EXIF extraction error codes
 */
export const EXIF_ERROR_CODES = {
  NO_EXIF_DATA: 'NO_EXIF_DATA',
  UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
  CORRUPTED_DATA: 'CORRUPTED_DATA',
  PARSING_FAILED: 'PARSING_FAILED',
  FILE_READ_ERROR: 'FILE_READ_ERROR',
} as const

/**
 * Standard EXIF orientation values
 */
export const EXIF_ORIENTATION = {
  1: 'Normal',
  2: 'Mirrored horizontally',
  3: 'Rotated 180°',
  4: 'Mirrored vertically', 
  5: 'Mirrored horizontally, rotated 270°',
  6: 'Rotated 90°',
  7: 'Mirrored horizontally, rotated 90°',
  8: 'Rotated 270°'
} as const